/*
  WYE — Back in stock (v3, Shopify Flow backend)
 
  What changed vs the Klaviyo version:
    - No Klaviyo. The email is sent to Shopify's own customer endpoint (/contact)
      with contact[tags] = "newsletter,BIS_VARIANT_<variantId>".
    - Submitted with fetch(), so the page never reloads and never redirects home.
    - Marketing consent checkbox is enforced, because Flow's "Send marketing email"
      action only delivers to customers whose email marketing state is Subscribed.
    - A size counts as out of stock when its tracked inventory is 0 or less, even if
      the variant is still buyable ("continue selling when out of stock").
 
  Root element contract (unchanged from v2 except company-id is no longer used):
    [data-wye-bis-root][data-wye-bis-size-option-index="N"]
    [data-abl-variants]                       JSON array of variants
    .abl-product-form [data-abl-variant-id]
    [data-abl-option-group] [data-abl-option-input]
    [data-wye-bis-open][aria-controls="<drawerId>"]
*/
 
(() => {
  'use strict';
 
  const ROOT_SELECTOR = '[data-wye-bis-root]';
  const TAG_PREFIX = 'BIS_VARIANT_';
  const stateMap = new WeakMap();
 
  function log(...args) {
    if (window.WYE_BIS_DEBUG) console.log('[WYE BIS]', ...args);
  }
 
  function validEmail(value) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim());
  }
 
  function parseVariants(root) {
    const element = root.querySelector('[data-abl-variants]');
    if (!element) return [];
    try {
      return JSON.parse(element.textContent || '[]');
    } catch (error) {
      console.error('WYE back in stock: product variants could not be read.', error);
      return [];
    }
  }
 
  function getDrawerForTrigger(trigger) {
    const id = trigger?.getAttribute('aria-controls');
    return id ? document.getElementById(id) : null;
  }
 
  function getRootFromTrigger(trigger) {
    return trigger?.closest(ROOT_SELECTOR) || null;
  }
 
  function getState(root) {
    if (!root) return null;
    let state = stateMap.get(root);
    if (state) return state;
 
    const productForm = root.querySelector('.abl-product-form');
    const trigger = root.querySelector('[data-wye-bis-open]');
    const variantInput = productForm?.querySelector('[data-abl-variant-id]') || null;
    const optionGroups = Array.from(productForm?.querySelectorAll('[data-abl-option-group]') || []);
    const variants = parseVariants(root);
    const sizeOptionIndex = Number(root.dataset.wyeBisSizeOptionIndex);
 
    if (!productForm || !trigger || !variants.length || Number.isNaN(sizeOptionIndex) || sizeOptionIndex < 0) {
      log('Not initialised', { productForm, trigger, variants: variants.length, sizeOptionIndex });
      return null;
    }
 
    state = { root, productForm, trigger, variantInput, optionGroups, variants, sizeOptionIndex };
    stateMap.set(root, state);
    return state;
  }
 
  function selectedOptions(state) {
    return state.optionGroups.map((group) => {
      const checked = group.querySelector('[data-abl-option-input]:checked');
      return checked ? checked.value : '';
    });
  }
 
  function exactVariant(state, values) {
    return state.variants.find((variant) =>
      variant.options.every((option, index) => String(option) === String(values[index]))
    );
  }
 
  function currentVariant(state) {
    const id = state.variantInput?.value;
    return state.variants.find((variant) => String(variant.id) === String(id))
      || exactVariant(state, selectedOptions(state))
      || null;
  }
 
  /* A size is "out of stock" when Shopify tracks its inventory and the quantity is
     0 or less. variant.available alone is not enough: with the inventory policy set
     to "continue selling when out of stock" a sold-out size still reports available. */
  function variantOutOfStock(variant) {
    if (!variant) return true;
 
    const tracked = Boolean(variant.inventoryManagement);
    const quantity = Number(variant.inventoryQuantity);
 
    if (tracked && Number.isFinite(quantity)) return quantity <= 0;
 
    return !variant.available;
  }
 
  function unavailableVariants(state) {
    const current = selectedOptions(state);
    const output = [];
 
    state.variants.forEach((variant) => {
      const sameOtherOptions = variant.options.every((option, index) => {
        if (index === state.sizeOptionIndex) return true;
        return String(option) === String(current[index]);
      });
 
      if (!sameOtherOptions || !variantOutOfStock(variant)) return;
 
      const size = String(variant.options[state.sizeOptionIndex] || '').trim();
      if (!size) return;
      if (output.some((item) => item.size === size)) return;
 
      output.push({ size, variant });
    });
 
    return output;
  }
 
  function updateTrigger(root) {
    const state = getState(root);
    if (!state) return;
 
    const unavailable = unavailableVariants(state);
    const hasSizes = unavailable.length > 0;
 
    state.trigger.hidden = !hasSizes;
    // The theme sets .wye-bis-trigger{display:inline-flex}, which overrides the
    // browser's [hidden] rule, so set display directly as well.
    state.trigger.style.display = hasSizes ? '' : 'none';
    state.trigger.setAttribute('aria-hidden', hasSizes ? 'false' : 'true');
    log('Trigger updated', unavailable.map((item) => item.size));
  }
 
  function drawerElements(drawer) {
    return {
      panel: drawer.querySelector('[data-wye-bis-panel]'),
      sizeOptions: drawer.querySelector('[data-wye-bis-size-options]'),
      emptyState: drawer.querySelector('[data-wye-bis-empty]'),
      form: drawer.querySelector('[data-wye-bis-form]'),
      email: drawer.querySelector('[data-wye-bis-email]'),
      consent: drawer.querySelector('[data-wye-bis-consent]'),
      submit: drawer.querySelector('[data-wye-bis-submit]'),
      message: drawer.querySelector('[data-wye-bis-message]'),
      success: drawer.querySelector('[data-wye-bis-success]'),
      previewSize: drawer.querySelector('[data-wye-bis-preview-size]')
    };
  }
 
  function setMessage(elements, text, info = false) {
    if (!elements.message) return;
    elements.message.textContent = text || '';
    elements.message.classList.toggle('is-info', Boolean(info));
  }
 
  function updatePreviewSize(elements) {
    if (!elements.sizeOptions || !elements.previewSize) return;
    const checked = elements.sizeOptions.querySelector('[data-wye-bis-size-radio]:checked');
    elements.previewSize.textContent = checked?.dataset.size || '';
  }
 
  function renderDrawerSizes(state, drawer) {
    const elements = drawerElements(drawer);
    if (!elements.sizeOptions) return [];
 
    const unavailable = unavailableVariants(state);
    const activeVariant = currentVariant(state);
 
    elements.sizeOptions.innerHTML = '';
 
    unavailable.forEach(({ size, variant }, index) => {
      const id = `${drawer.id}-size-${variant.id}`;
 
      const radio = document.createElement('input');
      radio.type = 'radio';
      radio.name = `${drawer.id}-variant`;
      radio.id = id;
      radio.value = String(variant.id);
      radio.className = 'wye-bis__size-radio';
      radio.dataset.wyeBisSizeRadio = '';
      radio.dataset.size = size;
 
      if (
        String(activeVariant?.id) === String(variant.id)
        || (!activeVariant?.available && index === 0)
      ) {
        radio.checked = true;
      }
 
      const label = document.createElement('label');
      label.htmlFor = id;
      label.className = 'wye-bis__size-label';
      label.textContent = size;
 
      elements.sizeOptions.append(radio, label);
    });
 
    const first = elements.sizeOptions.querySelector('[data-wye-bis-size-radio]');
    if (first && !elements.sizeOptions.querySelector('[data-wye-bis-size-radio]:checked')) {
      first.checked = true;
    }
 
    if (elements.emptyState) elements.emptyState.hidden = unavailable.length > 0;
    if (elements.submit) elements.submit.disabled = unavailable.length === 0;
    updatePreviewSize(elements);
 
    return unavailable;
  }
 
  function openDrawer(trigger) {
    const root = getRootFromTrigger(trigger);
    const state = getState(root);
    const drawer = getDrawerForTrigger(trigger);
 
    if (!state || !drawer) {
      console.warn('WYE back in stock: drawer could not be opened.', { state: Boolean(state), drawer: Boolean(drawer) });
      return;
    }
 
    const elements = drawerElements(drawer);
    const unavailable = renderDrawerSizes(state, drawer);
 
    if (!unavailable.length) {
      updateTrigger(root);
      return;
    }
 
    drawer.dataset.wyeBisRootId = root.id || '';
    drawer._wyePreviousFocus = document.activeElement;
 
    if (elements.form) elements.form.hidden = false;
    if (elements.success) elements.success.hidden = true;
    if (elements.email) elements.email.removeAttribute('aria-invalid');
    setMessage(elements, '');
 
    drawer.setAttribute('aria-hidden', 'false');
    drawer.classList.add('is-open');
    document.documentElement.classList.add('wye-bis-lock');
    document.body.classList.add('wye-bis-lock');
 
    requestAnimationFrame(() => {
      drawer.classList.add('is-open');
      window.setTimeout(() => elements.panel?.focus(), 40);
    });
 
    log('Drawer opened');
  }
 
  function closeDrawer(drawer) {
    if (!drawer) return;
 
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('wye-bis-lock');
    document.body.classList.remove('wye-bis-lock');
 
    const previous = drawer._wyePreviousFocus;
    window.setTimeout(() => previous?.focus?.(), 20);
  }
 
  function rootForDrawer(drawer) {
    const id = drawer?.dataset.wyeBisRootId;
    if (id) {
      const found = document.getElementById(id);
      if (found) return found;
    }
 
    const trigger = document.querySelector(`[data-wye-bis-open][aria-controls="${CSS.escape(drawer.id)}"]`);
    return trigger?.closest(ROOT_SELECTOR) || null;
  }
 
  function selectedDrawerVariant(state, elements) {
    const checked = elements.sizeOptions?.querySelector('[data-wye-bis-size-radio]:checked');
    if (!checked) return null;
    return state.variants.find((variant) => String(variant.id) === String(checked.value)) || null;
  }
 
  /* ---------------------------------------------------------------------------
     Finding a real Shopify customer form.
 
     Shopify answers "400" when a hand-built FormData is posted to /contact, so we
     always post a form that Shopify itself rendered. Order of preference:
       1. the hidden {% form 'customer' %} inside the drawer snippet
       2. any other customer form on the page (the footer newsletter form)
       3. a hand-built form as a last resort
  --------------------------------------------------------------------------- */
  function findTemplateForm(drawer) {
    const own = drawer.querySelector('.wye-bis__native-form');
    if (own) return own;
 
    const marker = document.querySelector('input[name="form_type"][value="customer"]');
    const borrowed = marker?.closest('form');
    if (borrowed) log('Using another customer form on the page as a template.');
    return borrowed || null;
  }
 
  /* Build a throwaway form so we never mutate the theme's footer form. Every
     hidden field Shopify put in the template is copied across. */
  function buildSubmission(drawer, email, tag) {
    const template = findTemplateForm(drawer);
    const form = document.createElement('form');
 
    form.method = 'post';
    form.action = template?.getAttribute('action') || '/contact#contact_form';
    form.acceptCharset = 'UTF-8';
    form.hidden = true;
 
    const add = (name, value) => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.value = value;
      form.appendChild(input);
    };
 
    const copied = new Set();
 
    if (template) {
      template.querySelectorAll('input[type="hidden"]').forEach((input) => {
        if (!input.name || input.name.startsWith('contact[') || input.name === 'return_to') return;
        add(input.name, input.value);
        copied.add(input.name);
      });
    }
 
    if (!copied.has('form_type')) add('form_type', 'customer');
    if (!copied.has('utf8')) add('utf8', '✓');
 
    add('contact[email]', email);
    add('contact[tags]', `newsletter,${tag}`);
    add('return_to', `${drawer.dataset.wyeBisReturnTo || window.location.pathname}?customer_posted=true`);
 
    document.body.appendChild(form);
    return { form, hadTemplate: Boolean(template) };
  }
 
  async function postToShopify(drawer, email, tag) {
    const { form, hadTemplate } = buildSubmission(drawer, email, tag);
 
    try {
      const response = await fetch(form.action, {
        method: 'POST',
        body: new FormData(form),
        headers: { Accept: 'text/html' },
        redirect: 'follow',
        credentials: 'same-origin'
      });
 
      const finalUrl = String(response.url || '');
      log('Shopify responded', response.status, finalUrl, { hadTemplate });
 
      // Captcha page, or Shopify refused the background post (common on preview
      // domains). The real form submit still works, so hand over to it.
      if (!response.ok || finalUrl.includes('/challenge')) {
        const error = new Error('FALLBACK');
        error.code = 'FALLBACK';
        error.form = form;
        throw error;
      }
 
      form.remove();
      return { ok: true };
    } catch (error) {
      if (error.code !== 'FALLBACK') form.remove();
      throw error;
    }
  }
 
  /* Last resort: submit for real. The page goes to Shopify and comes back to the
     product page through return_to. */
  function nativeFallback(error) {
    if (!error?.form) return false;
    error.form.submit();
    return true;
  }
 
  async function submitSubscription(form) {
    const drawer = form.closest('[data-wye-bis-drawer]');
    const root = rootForDrawer(drawer);
    const state = getState(root);
    const elements = drawerElements(drawer);
 
    if (!drawer || !state) {
      console.warn('WYE back in stock: missing product state during submit.');
      return;
    }
 
    setMessage(elements, '');
    elements.email?.removeAttribute('aria-invalid');
 
    const emailValue = String(elements.email?.value || '').trim();
    const variant = selectedDrawerVariant(state, elements);
 
    if (!variant) {
      setMessage(elements, 'Select the size you want to be notified about.');
      return;
    }
 
    if (!validEmail(emailValue)) {
      elements.email?.setAttribute('aria-invalid', 'true');
      setMessage(elements, 'Enter a valid email address.');
      elements.email?.focus();
      return;
    }
 
    if (elements.consent && !elements.consent.checked) {
      setMessage(elements, 'Tick the box so we can email you when this size is back.');
      elements.consent.focus();
      return;
    }
 
    const tag = `${TAG_PREFIX}${variant.id}`;
 
    elements.submit.disabled = true;
    elements.submit.classList.add('is-loading');
    setMessage(elements, 'Saving your request…', true);
 
    try {
      await postToShopify(drawer, emailValue, tag);
 
      if (elements.form) elements.form.hidden = true;
      if (elements.success) elements.success.hidden = false;
      if (elements.email) elements.email.value = '';
      if (elements.consent) elements.consent.checked = false;
      setMessage(elements, '');
      log('Subscribed', { email: emailValue, tag });
    } catch (error) {
      if (error.code === 'FALLBACK' && nativeFallback(error)) {
        setMessage(elements, 'One more step — finishing your request…', true);
        return;
      }
      console.error('WYE back in stock subscription failed.', error);
      setMessage(elements, error.message || 'We could not save your request. Try again.');
    } finally {
      elements.submit.classList.remove('is-loading');
      elements.submit.disabled = unavailableVariants(state).length === 0;
    }
  }
 
  function initialise(scope = document) {
    const roots = [];
 
    if (scope instanceof Element && scope.matches(ROOT_SELECTOR)) roots.push(scope);
    if (scope.querySelectorAll) roots.push(...scope.querySelectorAll(ROOT_SELECTOR));
 
    roots.forEach((root) => {
      getState(root);
      updateTrigger(root);
    });
  }
 
  document.addEventListener('click', (event) => {
    const openButton = event.target.closest?.('[data-wye-bis-open]');
    if (openButton) {
      event.preventDefault();
      event.stopPropagation();
      openDrawer(openButton);
      return;
    }
 
    const closeButton = event.target.closest?.('[data-wye-bis-close]');
    if (closeButton) {
      event.preventDefault();
      closeDrawer(closeButton.closest('[data-wye-bis-drawer]'));
    }
  }, true);
 
  document.addEventListener('change', (event) => {
    const productOption = event.target.closest?.('[data-abl-option-input]');
    if (productOption) {
      const root = productOption.closest(ROOT_SELECTOR);
      window.setTimeout(() => updateTrigger(root), 0);
      return;
    }
 
    const sizeRadio = event.target.closest?.('[data-wye-bis-size-radio]');
    if (sizeRadio) {
      const drawer = sizeRadio.closest('[data-wye-bis-drawer]');
      updatePreviewSize(drawerElements(drawer));
    }
  });
 
  document.addEventListener('submit', (event) => {
    const form = event.target.closest?.('[data-wye-bis-form]');
    if (!form) return;
    event.preventDefault();
    submitSubscription(form);
  });
 
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const openDrawerElement = document.querySelector('[data-wye-bis-drawer].is-open');
    if (openDrawerElement) closeDrawer(openDrawerElement);
  });
 
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialise(), { once: true });
  } else {
    initialise();
  }
 
  document.addEventListener('shopify:section:load', (event) => initialise(event.target));
 
  // Run WYE_BIS_DIAG() in the browser console to check wiring.
  window.WYE_BIS_DIAG = function () {
    const roots = Array.from(document.querySelectorAll(ROOT_SELECTOR));
    return roots.map((root) => {
      const state = getState(root);
      const trigger = root.querySelector('[data-wye-bis-open]');
      const drawer = trigger ? getDrawerForTrigger(trigger) : null;
      return {
        backend: 'Shopify /contact + customer tags + Flow',
        rootId: root.id,
        triggerFound: Boolean(trigger),
        triggerHidden: trigger ? trigger.hidden : null,
        drawerFound: Boolean(drawer),
        variants: state?.variants?.length || 0,
        sizeOptionIndex: state?.sizeOptionIndex,
        unavailableSizes: state ? unavailableVariants(state).map((item) => `${item.size} → ${TAG_PREFIX}${item.variant.id}`) : [],
        allSizes: state ? state.variants.map((variant) => ({
          title: variant.title,
          available: variant.available,
          tracked: Boolean(variant.inventoryManagement),
          quantity: variant.inventoryQuantity,
          countsAsOutOfStock: variantOutOfStock(variant)
        })) : []
      };
    });
  };
})();
 